import { Input } from 'app/agriculture/forms/components/Input/Input';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useCallback } from 'react';
import { Actions } from 'app/agriculture/components/Actions/Actions';
import { useNavigate } from 'react-router-dom';
import { Button, IconButton } from 'app/agriculture/components/Button/Button';
import './InviteUsersForm.scss';
import { useInviteUsersMutation } from 'app/agriculture/mutations/invitations/useInviteUsersMutation';
import { z } from 'zod';

interface NewUser {
    email: string;
    enabled: boolean;
}

interface InviteUsersFormFields {
    users: NewUser[];
}

function validateEmail(value: unknown) {
    if (z.string().email().safeParse(value).error) return 'Please enter a valid email address';
}

const newUser = { email: '', enabled: true };

export function InviteUsersForm() {
    const navigate = useNavigate();
    const form = useForm<InviteUsersFormFields>({
        mode: 'all',
        defaultValues: {
            users: [newUser],
        },
        shouldFocusError: true,
    });

    form.watch('users');

    const { mutate, isPending } = useInviteUsersMutation();

    const onSubmit: SubmitHandler<InviteUsersFormFields> = useCallback(
        ({ users }) => {
            mutate(
                users.filter(({ enabled }) => enabled),
                {
                    onSuccess: () => {
                        navigate('..', { replace: true, relative: 'path' });
                    },
                },
            );
        },
        [mutate, navigate],
    );

    const handleAddRow = useCallback(() => {
        const users = form.getValues('users');
        form.setValue('users', [...users, newUser]);
    }, [form]);

    const handleRemoveRow = useCallback(
        (index: number) => {
            const users = [...form.getValues('users')];
            users.splice(index, 1, { ...newUser, enabled: false });
            form.setValue('users', users);
        },
        [form],
    );

    return (
        <form className="InviteUsersForm" onSubmit={form.handleSubmit(onSubmit)}>
            <div className="InviteUsersForm__inner">
                {form.getValues('users').map(({ enabled }, index) => {
                    if (!enabled) return null;
                    const email = `users.${index}.email` as const;

                    return (
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={index} className="InviteUsersForm__row">
                            <Input
                                className="InviteUsersForm__email-input"
                                label="Email"
                                {...form.register(email, {
                                    required: 'Please enter a valid email address',
                                    validate: {
                                        validateEmail,
                                    },
                                })}
                                errorMessage={
                                    (form.formState.errors.users ?? [])[index]?.email?.message
                                }
                            />
                            {index === 0 ? null : (
                                <IconButton
                                    className="InviteUsersForm__delete-row-button"
                                    icon="close"
                                    tooltip="Delete this row"
                                    variant="textonly"
                                    onClick={() => {
                                        handleRemoveRow(index);
                                    }}
                                />
                            )}
                        </div>
                    );
                })}
                <Button
                    className="InviteUsersForm__add-another-user-button"
                    type="button"
                    onClick={handleAddRow}
                    startIcon="add"
                    text="Add Another User"
                    variant="textonly"
                />
            </div>
            <Actions
                sticky
                primaryAction={{
                    text: 'Send Invites',
                    isLoading: isPending,
                    type: 'submit',
                }}
                backAction={{
                    text: 'Back',
                    onClick: () => {
                        navigate(-1);
                    },
                }}
            />
        </form>
    );
}
